import type {
  BrowserCommand,
  BrowserDisplaySession,
  BrowserSessionSnapshot,
  BrowserStreamEvent,
  DevTunnelRecord,
  EnvironmentApi,
} from "@cmmd-forge/contracts";
import {
  preserveLocalPreviewTabsForMaintenanceCommand,
  resolveRemoteWebRtcInputTimeoutRecoveryDisplay,
  shouldRefreshBrowserSessionAfterCommand,
} from "./BrowserPreviewPanel.logic";

export type BrowserCommandRpcResult = Awaited<
  ReturnType<NonNullable<EnvironmentApi["browser"]>["command"]>
>;

export interface BrowserCommandErrorDecisionInput {
  readonly command: BrowserCommand;
  readonly error: unknown;
  readonly latestDisplay: BrowserDisplaySession | null;
  readonly useNativeBrowserCommand: boolean;
}

export type BrowserCommandErrorDecision =
  | {
      readonly type: "recover-display";
      readonly display: BrowserDisplaySession;
    }
  | {
      readonly type: "report-error";
    };

export function resolveBrowserCommandErrorDecision(
  input: BrowserCommandErrorDecisionInput,
): BrowserCommandErrorDecision {
  const recoveryDisplay = resolveRemoteWebRtcInputTimeoutRecoveryDisplay({
    command: input.command,
    error: input.error,
    useNativeBrowserCommand: input.useNativeBrowserCommand,
    display: input.latestDisplay,
  });

  if (recoveryDisplay) {
    return {
      type: "recover-display",
      display: recoveryDisplay,
    };
  }

  return { type: "report-error" };
}

export interface ServerBrowserCommandResultInput {
  readonly command: BrowserCommand;
  readonly previousSession: BrowserSessionSnapshot | null;
  readonly result: BrowserCommandRpcResult;
  readonly devTunnelRecords: readonly DevTunnelRecord[];
}

export interface ServerBrowserCommandResultDecision {
  readonly latestCapture: BrowserStreamEvent | null;
  readonly latestFrame: BrowserStreamEvent | null;
  readonly nextSession: BrowserSessionSnapshot;
  readonly shouldRefreshSession: boolean;
}

export function resolveServerBrowserCommandResult(
  input: ServerBrowserCommandResultInput,
): ServerBrowserCommandResultDecision {
  return {
    latestCapture: input.result.latestCapture ?? null,
    latestFrame: input.result.latestFrame ?? null,
    nextSession: preserveLocalPreviewTabsForMaintenanceCommand({
      command: input.command,
      previousSession: input.previousSession,
      nextSession: input.result.snapshot,
      devTunnelRecords: input.devTunnelRecords,
    }),
    shouldRefreshSession: shouldRefreshBrowserSessionAfterCommand(input.command),
  };
}
